import { useState } from "react";
import { api } from "../lib/api";
import { useApi } from "../lib/useApi";
import { useAuth } from "../lib/auth";
import { formatDate, formatNumber } from "../lib/format";
import {
  AzureUnavailable,
  Badge,
  Button,
  Card,
  ConfirmButton,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  PageHeader,
  Spinner,
  StatCard,
  Table,
  Td,
  Toggle,
} from "../components/ui";

interface Overview {
  totals: { total: number; last_24h: number; last_7d: number; internal: number; external: number };
  byStatus: { status: string; count: number }[];
  nearLimit: { email: string; name: string | null; requests_24h: number; daily_limit: number | null }[];
  defaultDailyLimit: string | null;
}

interface SettingRow {
  user_id: string;
  email: string;
  name: string | null;
  signing_enabled: number;
  external_enabled: number;
  daily_limit: number | null;
  updated_at: string;
  updated_by: string | null;
}

interface RequestRow {
  id: string;
  status: string;
  method: string;
  signer_count: number;
  signed_count: number;
  created_at: string;
  user_email: string;
  cert_number: string | null;
}

const STATUS_TONE: Record<string, "green" | "amber" | "blue" | "red" | "slate"> = {
  completed: "green",
  pending: "amber",
  partial: "blue",
  expired: "red",
  draft: "slate",
};

export default function Signing() {
  const { hasRole } = useAuth();
  const canWrite = hasRole("admin");
  const overview = useApi<Overview>("/signing/overview");
  const settings = useApi<{ settings: SettingRow[] }>("/signing/settings");
  const requests = useApi<{ requests: RequestRow[] }>("/signing/requests?limit=50");
  const azure = useApi<{ available: boolean; error?: string } & Record<string, unknown>>("/signing/azure");
  const [editing, setEditing] = useState<SettingRow | null>(null);
  const [limitDraft, setLimitDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function saveOverride(row: SettingRow, changes: Partial<{ signing_enabled: boolean; external_enabled: boolean; daily_limit: number | null }>) {
    setBusy(true);
    setActionError(null);
    try {
      await api.put(`/signing/settings/${row.user_id}`, changes);
      settings.reload();
      overview.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (overview.loading) return <Spinner />;
  if (overview.error || !overview.data) return <ErrorState message={overview.error ?? "Failed to load"} onRetry={overview.reload} />;

  const o = overview.data;

  return (
    <div>
      <PageHeader title="Signing" subtitle="Free-tier ceremonies, limits, and per-user overrides; paid platform usage below" />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Total requests" value={formatNumber(o.totals.total)} />
        <StatCard label="Last 24h" value={formatNumber(o.totals.last_24h)} />
        <StatCard label="Last 7d" value={formatNumber(o.totals.last_7d)} />
        <StatCard label="External" value={formatNumber(o.totals.external)} hint="Azure ceremonies" />
        <StatCard label="Internal" value={formatNumber(o.totals.internal)} hint="sealed locally" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card title={`Users near their daily limit (default ${o.defaultDailyLimit ?? "not set"})`}>
          {o.nearLimit.length === 0 ? (
            <EmptyState message="No external ceremonies in the last 24 hours." />
          ) : (
            <Table headers={["User", "Requests (24h)", "Limit"]}>
              {o.nearLimit.map((u) => (
                <tr key={u.email}>
                  <Td>{u.email}</Td>
                  <Td>{u.requests_24h}</Td>
                  <Td>{u.daily_limit ?? "default"}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>

        <Card title="Per-user overrides">
          {actionError && <ErrorState message={actionError} />}
          {settings.loading ? (
            <Spinner />
          ) : settings.data && settings.data.settings.length > 0 ? (
            <Table headers={["User", "Signing", "External", "Daily limit", canWrite ? "" : null].filter((h) => h !== null)}>
              {settings.data.settings.map((row) => (
                <tr key={row.user_id}>
                  <Td className="text-xs">{row.email}</Td>
                  <Td>
                    <Toggle
                      checked={Boolean(row.signing_enabled)}
                      disabled={!canWrite || busy}
                      onChange={(v) => saveOverride(row, { signing_enabled: v })}
                    />
                  </Td>
                  <Td>
                    <Toggle
                      checked={Boolean(row.external_enabled)}
                      disabled={!canWrite || busy}
                      onChange={(v) => saveOverride(row, { external_enabled: v })}
                    />
                  </Td>
                  <Td>
                    <button
                      className="text-rail-600 hover:underline disabled:text-slate-400"
                      disabled={!canWrite}
                      onClick={() => {
                        setEditing(row);
                        setLimitDraft(row.daily_limit === null ? "" : String(row.daily_limit));
                      }}
                    >
                      {row.daily_limit ?? "default"}
                    </button>
                  </Td>
                  {canWrite && (
                    <Td>
                      <ConfirmButton
                        label="Reset"
                        confirmLabel="Remove override?"
                        variant="secondary"
                        disabled={busy}
                        onConfirm={async () => {
                          setBusy(true);
                          try {
                            await api.delete(`/signing/settings/${row.user_id}`);
                            settings.reload();
                          } catch (err) {
                            setActionError(err instanceof Error ? err.message : String(err));
                          } finally {
                            setBusy(false);
                          }
                        }}
                      />
                    </Td>
                  )}
                </tr>
              ))}
            </Table>
          ) : (
            <EmptyState message="No per-user overrides — everyone is on the defaults. Overrides for a user can be added from their detail page once they have signing activity, or created here when a row exists." />
          )}
        </Card>
      </div>

      <div className="mt-6">
        <Card title="Recent signing requests">
          {requests.loading ? (
            <Spinner />
          ) : requests.data && requests.data.requests.length > 0 ? (
            <Table headers={["Certificate", "User", "Status", "Method", "Signers", "Created"]}>
              {requests.data.requests.map((r) => (
                <tr key={r.id}>
                  <Td className="font-mono text-xs">{r.cert_number ?? "—"}</Td>
                  <Td className="text-xs">{r.user_email}</Td>
                  <Td>
                    <Badge tone={STATUS_TONE[r.status] ?? "slate"}>{r.status}</Badge>
                  </Td>
                  <Td>{r.method}</Td>
                  <Td>
                    {r.signed_count}/{r.signer_count}
                  </Td>
                  <Td className="text-xs">{formatDate(r.created_at)}</Td>
                </tr>
              ))}
            </Table>
          ) : (
            <EmptyState message="No signing requests yet." />
          )}
        </Card>
      </div>

      <div className="mt-6">
        <Card title="Paid platform signing (Azure)">
          {azure.loading ? (
            <Spinner />
          ) : azure.data?.available ? (
            <pre className="overflow-x-auto rounded bg-slate-50 p-3 text-xs text-slate-700">
              {JSON.stringify(
                Object.fromEntries(Object.entries(azure.data).filter(([k]) => k !== "available")),
                null,
                2,
              )}
            </pre>
          ) : (
            <AzureUnavailable detail={azure.data?.error ?? azure.error ?? undefined} />
          )}
        </Card>
      </div>

      {editing && (
        <Modal title={`Daily limit — ${editing.email}`} onClose={() => setEditing(null)}>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Empty means the global default ({o.defaultDailyLimit ?? "not set"}).
            </p>
            <Input
              type="number"
              min={0}
              value={limitDraft}
              onChange={(e) => setLimitDraft(e.target.value)}
              placeholder="default"
            />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button
                disabled={busy}
                onClick={async () => {
                  await saveOverride(editing, {
                    daily_limit: limitDraft.trim() === "" ? null : Number(limitDraft),
                  });
                  setEditing(null);
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
